import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentVisibility } from '../../api/data-contracts';
import { log } from '../../utils/errors/logger';
import { RecipientUtils } from '../../components/ui/RecipientPicker/types';
import {
  useCreateFolderSync,
  useUpdateFolderSync,
  type FolderSyncCreate,
  type FolderSyncUpdate
} from '../api/useFolderSyncs';
import { useShareFolderForm } from './useShareFolderForm';
import { useShareFolderValidation } from './useShareFolderValidation';
import {
  ShareFolderDialogProps,
  FileInfo,
  FolderSharingState,
} from '../../components/dialogs/ShareFolderDialog/types';

/**
 * Main orchestration hook for ShareFolderDialog
 * Combines form management, validation, and submission logic
 *
 * @param props - Dialog props
 * @returns All state and handlers needed by the dialog
 */
export const useShareFolderDialog = ({
  open,
  mode,
  initialData,
  onSuccess,
}: ShareFolderDialogProps) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Initialize state based on mode
  const getInitialState = useCallback((): FolderSharingState => {
    if (mode === 'edit' && initialData) {
      return {
        url: initialData.url,
        name: initialData.name,
        description: initialData.description || '',
        selectedFiles: initialData.configuration.selected_files || [],
        syncStatus: 'idle',
        sharingData: {
          visibility: initialData.configuration.default_visibility as DocumentVisibility,
          recipientSelection: {
            users: initialData.configuration.default_shared_with_users || [],
            groups: initialData.configuration.default_shared_with_groups || [],
          },
          purpose: '',
        },
      };
    }
    return {
      url: '',
      name: '',
      description: '',
      selectedFiles: [],
      syncStatus: 'idle',
      sharingData: {
        visibility: 'private' as DocumentVisibility,
        recipientSelection: RecipientUtils.empty(),
        purpose: '',
      },
    };
  }, [mode, initialData]);

  // Form management hook
  const {
    formState,
    handleUrlChange,
    handleNameChange,
    handleDescriptionChange,
    handleSharingChange,
    handleFileToggle,
    handleSelectAll,
    handleSelectNone,
    setFormState,
    resetForm,
  } = useShareFolderForm(getInitialState());

  // Validation hook
  const {
    validateUrl,
    detectProvider,
    isValidating,
  } = useShareFolderValidation();

  // Mutations
  const createMutation = useCreateFolderSync();
  const updateMutation = useUpdateFolderSync();

  /**
   * Reset state when dialog opens/closes or mode changes
   */
  useEffect(() => {
    if (open) {
      setFormState(getInitialState());
      setSubmitError(null);
      setSubmitting(false);
      // Edit mode: Skip URL entry, go directly to file selection
      // Create mode: Start from URL entry
      setActiveStep(mode === 'edit' ? 1 : 0);

      // For edit mode, automatically load files from the existing URL
      if (mode === 'edit' && initialData) {
        setFormState(prev => ({ ...prev, syncStatus: 'fetching' }));
        validateUrl.mutate({ url: initialData.url });
      }
    }
  }, [open, mode, initialData]);

  /**
   * Handle URL validation
   */
  const handleValidateUrl = useCallback(() => {
    if (!formState.url.trim()) {
      setFormState(prev => ({
        ...prev,
        syncStatus: 'error',
        errorMessage: t('shareFolder.errors.invalidLink'),
      }));
      return;
    }

    setFormState(prev => ({ ...prev, syncStatus: 'validating' }));
    validateUrl.mutate(
      { url: formState.url },
      {
        onSuccess: (data) => {
          if (!data.is_valid) {
            setFormState(prev => ({
              ...prev,
              syncStatus: 'error',
              errorMessage: data.error_message || t('shareFolder.errors.invalidOrInaccessible'),
            }));
            return;
          }

          setFormState(prev => ({
            ...prev,
            syncStatus: 'idle',
            folderInfo: data.folder_info ? {
              provider: data.provider || 'unknown',
              folderName: data.folder_info.folder_name || 'Unknown',
              fileCount: data.folder_info.total_files || 0,
              totalSize: data.folder_info.total_size || 0,
            } : undefined,
            files: (data.files ?? []) as FileInfo[],
          }));
          setActiveStep(1);
        },
        onError: () => {
          setFormState(prev => ({
            ...prev,
            syncStatus: 'error',
            errorMessage: t('shareFolder.errors.invalidOrInaccessible'),
          }));
        },
      }
    );
  }, [formState.url, t, validateUrl, setFormState]);

  /**
   * Handle form submission (create or update)
   */
  const handleSave = useCallback(async () => {
    if (!formState.name.trim()) {
      setSubmitError(t('shareFolder.errors.nameRequired'));
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setFormState(prev => ({ ...prev, syncStatus: 'processing' }));

    try {
      // Prepare data for API
      const sharedWithUsers = formState.sharingData.recipientSelection?.users || [];
      const sharedWithGroups = formState.sharingData.recipientSelection?.groups || [];

      if (mode === 'create') {
        // Create mode: Include all configuration
        const folderSyncData = {
          name: formState.name,
          provider: detectProvider(formState.url),
          url: formState.url,
          description: formState.description || undefined,
          configuration: {
            ssl_verify: true,
            sync_subfolders: true,
            default_visibility: formState.sharingData.visibility,
            default_shared_with_groups: sharedWithGroups,
            default_shared_with_users: sharedWithUsers,
            remove_deleted_files: false,
            update_changed_files: true,
            preserve_user_changes: false,
            ...(formState.selectedFiles.length > 0 && { selected_files: formState.selectedFiles }),
          },
        };

        await createMutation.mutateAsync(folderSyncData as FolderSyncCreate);
      } else {
        // Edit mode: Update configuration and trigger sync changes
        const updateData = {
          name: formState.name,
          description: formState.description || undefined,
          configuration: {
            ...initialData!.configuration, // Keep existing config
            default_visibility: formState.sharingData.visibility,
            default_shared_with_groups: sharedWithGroups,
            default_shared_with_users: sharedWithUsers,
            selected_files: formState.selectedFiles, // Update file selection
          },
        };

        await updateMutation.mutateAsync({ id: initialData!.id, data: updateData as FolderSyncUpdate });
      }

      setFormState(prev => ({ ...prev, syncStatus: 'complete' }));
      onSuccess?.();
      setTimeout(() => {
        // onClose will be handled by parent
      }, 1500);
    } catch (error: unknown) {
      const errorMessage = mode === 'create'
        ? t('shareFolder.errors.importFailed')
        : 'Failed to update folder sync configuration';
      const detailedMessage = error instanceof Error ? error.message : errorMessage;
      setSubmitError(detailedMessage);
      setFormState(prev => ({
        ...prev,
        syncStatus: 'error',
        errorMessage: errorMessage,
      }));
      log.api.error(mode === 'create' ? 'POST' : 'PUT', '/api/folder-syncs/', error as Error);
    } finally {
      setSubmitting(false);
    }
  }, [formState, mode, initialData, t, createMutation, updateMutation, detectProvider, onSuccess, setFormState]);

  /**
   * Determine step actions state
   */
  const canProceedStep1 = formState.url.trim() && formState.syncStatus !== 'validating';
  const canProceedStep2 = true; // File selection is optional
  const canSubmit = formState.name.trim() && !submitting && formState.syncStatus !== 'processing';

  /**
   * Combine all loading states
   */
  const isLoading = submitting ||
    validateUrl.isPending ||
    createMutation.isPending ||
    updateMutation.isPending ||
    formState.syncStatus === 'validating' ||
    formState.syncStatus === 'fetching' ||
    formState.syncStatus === 'processing';

  /**
   * Combine all error states
   */
  const dialogError = submitError ||
    (validateUrl.error instanceof Error ? validateUrl.error.message : null) ||
    (createMutation.error instanceof Error ? createMutation.error.message : null) ||
    (updateMutation.error instanceof Error ? updateMutation.error.message : null) ||
    formState.errorMessage ||
    null;

  /**
   * Success message
   */
  const successMessage = formState.syncStatus === 'complete'
    ? (mode === 'edit' ? t('shareFolder.updateSuccess') : t('shareFolder.successMessage'))
    : null;

  return {
    // Mode
    isEditMode: mode === 'edit',

    // Form state
    formState,
    handleUrlChange,
    handleNameChange,
    handleDescriptionChange,
    handleSharingChange,
    handleFileToggle,
    handleSelectAll,
    handleSelectNone,

    // Validation
    handleValidateUrl,
    isValidating,
    detectProvider,

    // Stepper
    activeStep,
    setActiveStep,

    // Submission
    handleSave,
    submitting,
    submitError,

    // Validation flags
    canProceedStep1,
    canProceedStep2,
    canSubmit,

    // Combined states
    isLoading,
    dialogError,
    successMessage,

    // Reset
    resetForm,
  };
};
